/**
 * What the tab plays, decided in the browser — plan §9.4, §9.5, §9.8 and §9.9 as pure functions, on each of
 * the two layers the tab plays at once (the soundtrack, and the vehicle under it: {@link AUDIO_LAYERS}).
 *
 * WHY THESE LIVE HERE AND NOT IN THE PAGE. The mod sends WHERE a player is and never WHAT should play
 * (§9.4: "so the mod never has to know which area won"). Every rule that turns a context into a sound
 * is therefore a browser rule, and each one is the kind of thing that is easy to get subtly wrong and
 * cheap to pin with a table of cases: which area wins a tie, where a late joiner of a shared playback
 * starts, which language a voiced line is in. So they are functions of their inputs — no clock, no
 * randomness that is not seeded, no DOM, no storage — and `audioplay.test.ts` holds them to it.
 *
 * WHAT IS NOT HERE: anything that makes a noise. The engine in `app/src/audio/` asks these functions
 * and does what they say.
 */
import {
  normaliseAudioLang,
  type AreaDef,
  type AreaOrder,
  type AudioCatalog,
  type AudioContextMessage,
  type AudioInstanceRef,
  type AudioRide,
  type PlayerAudioContext,
} from './audio';

// ------------------------------------------------------------------ which area (§9.4)

/** Whether one area's trigger matches a context. `default` always does. */
export function areaMatches(area: AreaDef, context: PlayerAudioContext): boolean {
  const trigger = area.trigger;
  switch (trigger.kind) {
    case 'default':
      return true;
    case 'dimension':
      return context.dimension === trigger.id;
    case 'district':
      return context.district === trigger.id;
    case 'base':
      return context.base === true;
    case 'battle':
      // EMPTY MEANS ANY BATTLE (see AreaTrigger) — and no battle never matches a battle area.
      return context.battle !== undefined && (trigger.battles.length === 0 || trigger.battles.includes(context.battle));
    case 'biome':
      // EMPTY MATCHES NOTHING (see AreaTrigger), which `includes` already says.
      return context.biome !== undefined && trigger.biomes.includes(context.biome);
    case 'ride': {
      const ride = context.ride;
      if (ride === undefined) return false;
      // EMPTY MEANS ANY RIDE — including the one the mod cannot name (`""`), which no list can name.
      if (trigger.rides.length > 0 && !trigger.rides.includes(ride.id)) return false;
      if (trigger.motion !== undefined && (ride.moving === true) !== (trigger.motion === 'moving')) return false;
      return trigger.water === undefined || (ride.water === true) === (trigger.water === 'in');
    }
  }
}

/**
 * THE TWO LAYERS THE TAB PLAYS AT ONCE. `main` is the soundtrack — every area that is not a vehicle area, a
 * shared instance outranking them all (§9.9). `vehicle` is the sound of the ride the player sits on, chosen
 * among `vehicle` areas alone and played over the main layer rather than instead of it: a battle, an instance
 * and a change of zone each leave it exactly where it is.
 */
export const AUDIO_LAYERS = ['main', 'vehicle'] as const;
export type AudioLayer = (typeof AUDIO_LAYERS)[number];

/** Which layer an area plays on: `vehicle` areas on the vehicle layer, every other category on the main one. */
export function layerOf(area: Pick<AreaDef, 'category'>): AudioLayer {
  return area.category === 'vehicle' ? 'vehicle' : 'main';
}

/** The highest-priority matching area of one layer, ties to the earlier area. */
function resolveOn(layer: AudioLayer, areas: readonly AreaDef[], context: PlayerAudioContext | null): AreaDef | null {
  if (context === null) return null;
  let best: AreaDef | null = null;
  for (const area of areas) {
    if (layerOf(area) !== layer) continue;
    // Strictly greater: an equal priority later in the list never displaces an earlier one.
    if (areaMatches(area, context) && (best === null || area.priority > best.priority)) best = area;
  }
  return best;
}

/**
 * The MAIN layer: the highest-priority area whose trigger matches, ties to the EARLIER area in catalog order
 * (§9.4) — and never a `vehicle` area, whatever its priority: those are the other layer's. Null when nothing
 * matches, which is silence — a catalog with no `default` area is allowed to be quiet.
 */
export function resolveArea(areas: readonly AreaDef[], context: PlayerAudioContext | null): AreaDef | null {
  return resolveOn('main', areas, context);
}

/**
 * The VEHICLE layer: the highest-priority matching area AMONG `vehicle` AREAS ONLY, ties to the earlier one.
 * Null — silence on this layer — when none matches, which is every moment the player is not on a ride. Any
 * trigger kind may be a vehicle area's; in practice it is `ride`.
 */
export function resolveVehicleArea(areas: readonly AreaDef[], context: PlayerAudioContext | null): AreaDef | null {
  return resolveOn('vehicle', areas, context);
}

/** What should be playing, and the key that says whether it is the same thing as before. */
export interface PlaybackChoice {
  readonly area: AreaDef;
  /** Present while a shared playback (§9.9) decides the area and the timeline. */
  readonly instance: AudioInstanceRef | null;
  /**
   * Two choices with the same key are the SAME playback: a resync, a reconnect or a repeated context
   * frame that lands on it changes nothing audible (§9.4 "does not restart it").
   *
   * The key carries the area's `order` and `tracks` but not its name, volume or fade: a config reload
   * that rewrites an area's music is a change worth a crossfade, one that renames it or turns it down
   * is not. An instance's key carries its `startedAtMs`, so a NEW run of the same ride is a new key
   * even when the id is reused.
   */
  readonly key: string;
}

function areaKey(area: AreaDef): string {
  return `${area.id}|${area.order}|${area.tracks.join(',')}`;
}

/** What {@link choosePlayback} and {@link chooseVehiclePlayback} read: the parts of a `context` frame that decide. */
export type PlaybackMessage = Pick<AudioContextMessage, 'context' | 'online' | 'instance'>;

/**
 * The MAIN layer: the §9.4 resolution plus §9.9's override. While the context names an instance whose area
 * the catalog has, the instance plays — it outranks every personal area. An instance naming an area this
 * catalog does not have, or a vehicle area (which is not this layer's to play), falls back to the personal
 * resolution rather than to silence: the player is still somewhere, and a misconfigured instance should not
 * mute them.
 *
 * Offline is always silence, whatever the last context said.
 */
export function choosePlayback(catalog: AudioCatalog | null, message: PlaybackMessage | null): PlaybackChoice | null {
  if (catalog === null || message === null || !message.online) return null;
  const instance = message.instance;
  if (instance !== undefined) {
    const area = catalog.areas.find((candidate) => candidate.id === instance.area && layerOf(candidate) === 'main');
    if (area !== undefined) return { area, instance, key: `instance:${instance.id}@${instance.startedAtMs}:${areaKey(area)}` };
  }
  const area = resolveArea(catalog.areas, message.context);
  return area === null ? null : { area, instance: null, key: `area:${areaKey(area)}` };
}

/**
 * The VEHICLE layer: {@link resolveVehicleArea} on the context, and nothing else. A shared instance does not
 * reach it (`instance` is always null here), and neither does a battle — a battle is a main-layer area — so
 * whatever the main layer does, this one carries on. Offline is silence here too.
 */
export function chooseVehiclePlayback(catalog: AudioCatalog | null, message: PlaybackMessage | null): PlaybackChoice | null {
  if (catalog === null || message === null || !message.online) return null;
  const area = resolveVehicleArea(catalog.areas, message.context);
  return area === null ? null : { area, instance: null, key: `vehicle:${areaKey(area)}` };
}

/** Both layers' choices, each decided on its own: the main layer's never moves the vehicle layer's, nor back. */
export interface LayeredPlayback {
  readonly main: PlaybackChoice | null;
  readonly vehicle: PlaybackChoice | null;
}

/** What should be playing on each layer (see {@link AUDIO_LAYERS}). */
export function choosePlaybacks(catalog: AudioCatalog | null, message: PlaybackMessage | null): LayeredPlayback {
  return { main: choosePlayback(catalog, message), vehicle: chooseVehiclePlayback(catalog, message) };
}

// --------------------------------------------------------------- the timeline (§9.9)

/** A track as the timeline sees it: an id and how long it plays. */
export interface TimelineTrack {
  readonly id: string;
  /** Milliseconds. A track with no usable duration cannot be placed on a shared timeline and is skipped. */
  readonly durationMs: number;
}

/** Where on an area's track list a given instant falls. */
export type TimelinePoint =
  | {
      readonly kind: 'playing';
      /** How many times the list has gone round before this one (0 for the first pass). */
      readonly round: number;
      /** Position in this round's play order. */
      readonly slot: number;
      readonly trackId: string;
      /** How far into the track, in milliseconds. */
      readonly offsetMs: number;
    }
  /** A `once` area that has played to the end: silence until the area changes (§9.9). */
  | { readonly kind: 'finished' }
  /** No track with a duration: nothing to place. */
  | { readonly kind: 'empty' };

/**
 * A 32-bit seed from text — FNV-1a, the same arithmetic `app/src/me/identity.ts` uses for avatars.
 * Not a security primitive; it only has to give every member of an instance the SAME shuffle.
 */
export function seedOf(text: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
  }
  return hash >>> 0;
}

/** mulberry32: four lines, a full 32-bit period, and identical output in every JavaScript engine. */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function rawShuffle(count: number, seed: number, round: number): number[] {
  const indices = Array.from({ length: count }, (_, index) => index);
  const random = seededRandom((seed ^ Math.imul(round + 1, 0x9e3779b1)) >>> 0);
  for (let index = count - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [indices[index], indices[other]] = [indices[other] as number, indices[index] as number];
  }
  return indices;
}

/**
 * The play order of one round, as indices into the area's tracks.
 *
 * `shuffle` never plays the same track twice in a row across a round boundary. With three or more
 * tracks the fix is a swap of the round's first two entries, which never moves its LAST — so the
 * previous round's last track is computable without walking every round before it, and an instance
 * that has been running for a week costs the same as one that started a second ago. Two tracks have
 * only one order that never repeats at the boundary, so a two-track shuffle is a seeded fixed order.
 */
export function playOrderForRound(order: AreaOrder, count: number, round: number, seed: number): number[] {
  if (count <= 0) return [];
  if (order === 'loop') return [0];
  if (order !== 'shuffle' || count === 1) return Array.from({ length: count }, (_, index) => index);
  if (count === 2) return (seed & 1) === 0 ? [0, 1] : [1, 0];
  const indices = rawShuffle(count, seed, round);
  if (round > 0) {
    const previousLast = rawShuffle(count, seed, round - 1)[count - 1];
    if (indices[0] === previousLast) [indices[0], indices[1]] = [indices[1] as number, indices[0] as number];
  }
  return indices;
}

/**
 * Where `elapsedMs` into an area's timeline falls (§9.9: "seeked to correctedNow − startedAtMs").
 *
 * `loop` repeats the first track, so the answer is the elapsed time modulo its length. `sequence` and
 * `shuffle` walk the list round after round, each round in {@link playOrderForRound}'s order. `once`
 * walks it once and is then `finished`. A negative elapsed time — a start a few milliseconds in the
 * future, which clock error can produce — is the very beginning rather than a refusal.
 */
export function timelineAt(order: AreaOrder, tracks: readonly TimelineTrack[], elapsedMs: number, seed: number): TimelinePoint {
  const placed = tracks.filter((track) => Number.isFinite(track.durationMs) && track.durationMs > 0);
  if (placed.length === 0) return { kind: 'empty' };
  const elapsed = Math.max(0, Number.isFinite(elapsedMs) ? elapsedMs : 0);

  if (order === 'loop') {
    const first = placed[0] as TimelineTrack;
    return { kind: 'playing', round: Math.floor(elapsed / first.durationMs), slot: 0, trackId: first.id, offsetMs: elapsed % first.durationMs };
  }

  const total = placed.reduce((sum, track) => sum + track.durationMs, 0);
  if (order === 'once' && elapsed >= total) return { kind: 'finished' };
  const round = order === 'once' ? 0 : Math.floor(elapsed / total);
  let within = order === 'once' ? elapsed : elapsed % total;
  const playOrder = playOrderForRound(order, placed.length, round, seed);
  for (let slot = 0; slot < playOrder.length; slot += 1) {
    const track = placed[playOrder[slot] as number] as TimelineTrack;
    if (within < track.durationMs) return { kind: 'playing', round, slot, trackId: track.id, offsetMs: within };
    within -= track.durationMs;
  }
  // Floating-point dust at the very end of a round: the last slot, at its end.
  const last = placed[playOrder[playOrder.length - 1] as number] as TimelineTrack;
  return { kind: 'playing', round, slot: playOrder.length - 1, trackId: last.id, offsetMs: last.durationMs };
}

/** The seed every member of one instance shuffles with: its id and its start, nothing local. */
export function instanceSeed(instance: AudioInstanceRef): number {
  return seedOf(`${instance.id}@${instance.startedAtMs}`);
}

// ---------------------------------------------------------------------- voice (§9.8)

/**
 * The ONE language voiced lines play in: the context's own `lang` when the server sends one (it is
 * reserved for the day different players read different languages), else the catalog's — the
 * language of the text the server shows. NEVER the browser's: the line has to match what the player
 * reads in game, and only the server knows that. Null before the catalog has arrived.
 */
export function voiceLanguage(catalog: Pick<AudioCatalog, 'lang'> | null, context: PlayerAudioContext | null): string | null {
  return normaliseAudioLang(context?.lang) ?? normaliseAudioLang(catalog?.lang) ?? null;
}

/**
 * The manifest key of one clip in one language: `voice/<lang>/<clip>`, with the language spelled the way
 * the library pipeline spells it (`scripts/audio/audiokit/catalog.py`: `en`, `pt_br`) — an underscore,
 * where this package's normalised tags use a hyphen.
 */
export function voiceTrackId(lang: string, clip: string): string {
  return `voice/${lang.replace(/-/g, '_')}/${clip}`;
}

/** The language of a voice track id, normalised like every other tag here (`pt_br` → `pt-br`), or null for a track that is not a voiced line. */
export function voiceTrackLang(trackId: string): string | null {
  const match = /^voice\/([^/]+)\//.exec(trackId);
  return match === null ? null : normaliseAudioLang(match[1]);
}

/**
 * The track to play for a voiced `clip`, or NULL — silence — when the manifest has no clip in exactly
 * that language. There is no fallback to another language and there must never be one (§9.8): a line
 * in a language the player is not reading is worse than no line, and the library validator is what
 * reports the gap.
 */
export function resolveVoiceTrack(manifest: AudioManifest, lang: string | null, clip: string): string | null {
  if (lang === null) return null;
  const id = voiceTrackId(lang, clip);
  return Object.hasOwn(manifest, id) ? id : null;
}

// ------------------------------------------------------------------- the manifest (§9.7)

/**
 * One library entry. `ogg` and `mp3` are paths of content-hashed files — relative to the library in
 * `manifest.json`, rewritten to the deployed `/audio/…` URLs by the build. `durationMs` 0 means
 * unknown; `loudnessLufs` null means unmeasured. CC-BY tracks need `title`, `artist`, `license` and
 * `sourceUrl` shown, which is why all four are always present (possibly empty) rather than optional.
 */
export interface AudioManifestEntry {
  readonly ogg: string;
  readonly mp3: string;
  readonly title: string;
  readonly artist: string;
  readonly license: string;
  readonly sourceUrl: string;
  readonly loop: boolean;
  readonly durationMs: number;
  readonly loudnessLufs: number | null;
}

/** Track id → entry. Voice clips are entries too, keyed `voice/<lang>/<clip>`. */
export type AudioManifest = Readonly<Record<string, AudioManifestEntry>>;

/**
 * A file reference a manifest may hold: a relative path inside the library (`tracks/x.1a2b.ogg`) or the
 * deployed absolute one (`/audio/tracks/x.1a2b.ogg`). No scheme, no `..`, no protocol-relative `//` —
 * a manifest names files the build ships, never somewhere else on the internet.
 */
export function isManifestFilePath(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 256) return false;
  if (value.startsWith('//') || !/^[A-Za-z0-9_./-]+$/.test(value)) return false;
  return !value.split('/').some((segment) => segment === '..' || segment === '.');
}

function shortText(value: unknown, max = 200): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

/**
 * The manifest, keeping every entry it can read. An entry needs at least one of its two files; a
 * missing `sourceUrl` or one that is not http(s) reads as empty rather than as a link to anywhere.
 */
export function parseAudioManifest(value: unknown): { readonly manifest: AudioManifest; readonly problems: readonly string[] } {
  const problems: string[] = [];
  const manifest: Record<string, AudioManifestEntry> = {};
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return { manifest, problems: ['the manifest is not a JSON object'] };
  for (const [id, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!/^[A-Za-z0-9_.:/-]{1,128}$/.test(id)) {
      problems.push(`${JSON.stringify(id.slice(0, 64))} is not a usable track id`);
      continue;
    }
    if (typeof raw !== 'object' || raw === null) {
      problems.push(`${id}: not an object`);
      continue;
    }
    const e = raw as Record<string, unknown>;
    const ogg = isManifestFilePath(e['ogg']) ? e['ogg'] : '';
    const mp3 = isManifestFilePath(e['mp3']) ? e['mp3'] : '';
    if (ogg === '' && mp3 === '') {
      problems.push(`${id}: neither ogg nor mp3 names a file in the library`);
      continue;
    }
    const sourceUrl = typeof e['sourceUrl'] === 'string' && /^https?:\/\/[^\s"'<>]+$/.test(e['sourceUrl']) ? e['sourceUrl'] : '';
    const durationMs = typeof e['durationMs'] === 'number' && Number.isFinite(e['durationMs']) && e['durationMs'] > 0 ? e['durationMs'] : 0;
    const lufs = e['loudnessLufs'];
    manifest[id] = {
      ogg,
      mp3,
      title: shortText(e['title']) || id,
      artist: shortText(e['artist']),
      license: shortText(e['license'], 80),
      sourceUrl,
      loop: e['loop'] === true,
      durationMs,
      loudnessLufs: typeof lufs === 'number' && Number.isFinite(lufs) ? lufs : null,
    };
  }
  return { manifest, problems };
}

/** The two encodings the library ships (§9.7). */
export type AudioFormat = 'ogg' | 'mp3';

/**
 * Ogg Vorbis where the browser plays it, MP3 where it does not (Safari) — §9.7's rule, asked of the
 * browser rather than guessed from a user agent.
 */
export function preferredAudioFormat(canPlayType: (mime: string) => string): AudioFormat {
  return canPlayType('audio/ogg; codecs="vorbis"') === '' ? 'mp3' : 'ogg';
}

/** The file to fetch for an entry in the preferred format, falling back to the other one if it is the only one. */
export function trackFile(entry: AudioManifestEntry, format: AudioFormat): string | null {
  const preferred = format === 'ogg' ? entry.ogg : entry.mp3;
  const other = format === 'ogg' ? entry.mp3 : entry.ogg;
  return preferred !== '' ? preferred : other !== '' ? other : null;
}

// ---------------------------------------------------------------------- volume (§9.5)

/**
 * The loudness each kind of track is levelled to, in LUFS — the library pipeline's own targets
 * (`scripts/audio/audiokit/catalog.py`, plan §9.7): areas -16, stings -15, cues -14, voice -16. The kinds
 * differ on purpose: a cue sits a little above the music it interrupts, so flattening them all to one
 * number here would undo the mix the pipeline made.
 */
export const AUDIO_TARGET_LUFS = { area: -16, sting: -15, cue: -14, voice: -16 } as const;
export type AudioLoudnessKind = keyof typeof AUDIO_TARGET_LUFS;

/** Which target a track answers to, by the pipeline's rule: its key's shape (`voice/…`, `cue_…`, `sting_…`, anything else an area). */
export function loudnessKindOf(trackId: string): AudioLoudnessKind {
  if (trackId.startsWith('voice/')) return 'voice';
  if (trackId.startsWith('cue_')) return 'cue';
  if (trackId.startsWith('sting_')) return 'sting';
  return 'area';
}

/**
 * The gain that brings a track to its kind's target — ATTENUATE ONLY.
 *
 * The shipped library is levelled to exactly {@link AUDIO_TARGET_LUFS}, so for it this is 1 everywhere;
 * it is the guard for a track dropped in unlevelled (a royalty-free master can arrive anywhere from -9
 * to -20 LUFS, and a district change into a loud one is a jump scare). A quieter-than-target track is
 * left alone rather than boosted: a boost above unity is how a track with honest peaks clips, and a
 * slightly quiet track is the better failure. Unmeasured is unity.
 */
export function loudnessGain(lufs: number | null | undefined, kind: AudioLoudnessKind = 'area'): number {
  if (typeof lufs !== 'number' || !Number.isFinite(lufs)) return 1;
  return Math.min(1, Math.max(0.1, 10 ** ((AUDIO_TARGET_LUFS[kind] - lufs) / 20)));
}

/**
 * The per-device sliders (§9.5), in the order the page draws them. `vehicle` is the vehicle layer's and the
 * vehicle cues'. `voice` is reserved for §3's NPC lines and has no slider until they ship.
 */
export const AUDIO_VOLUME_CATEGORIES = ['master', 'music', 'battle', 'vehicle', 'alerts', 'voice'] as const;
export type AudioVolumeCategory = (typeof AUDIO_VOLUME_CATEGORIES)[number];

/** Slider positions, 0..1. A position, not a gain — see {@link sliderGain}. */
export type AudioVolumes = Readonly<Record<AudioVolumeCategory, number>>;

/**
 * Where a slider starts on a device that has never moved it — and where `vehicle` starts on one that saved its
 * sliders before there was a vehicle slider ({@link normaliseAudioVolumes} fills in what storage lacks).
 */
export const DEFAULT_AUDIO_VOLUMES: AudioVolumes = { master: 0.8, music: 0.7, battle: 0.85, vehicle: 0.8, alerts: 0.75, voice: 1 };

/** Whatever was in storage, as volumes: unknown keys ignored, missing or broken ones defaulted. */
export function normaliseAudioVolumes(value: unknown): AudioVolumes {
  const raw = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  const out: Record<string, number> = {};
  for (const category of AUDIO_VOLUME_CATEGORIES) {
    const position = raw[category];
    out[category] = typeof position === 'number' && Number.isFinite(position) ? Math.min(Math.max(position, 0), 1) : DEFAULT_AUDIO_VOLUMES[category];
  }
  return out as AudioVolumes;
}

/**
 * A slider position as a gain: squared, so the first half of the slider is not all of the audible
 * range. Loudness is roughly logarithmic in amplitude, and a linear slider spends its whole top half on
 * differences nobody hears.
 */
export function sliderGain(position: number): number {
  const clamped = Math.min(Math.max(Number.isFinite(position) ? position : 0, 0), 1);
  return clamped * clamped;
}

/**
 * Which slider an area answers to: `battle` areas to battle, `vehicle` areas to vehicle, ambient and music
 * areas to music (§9.5).
 */
export function volumeCategoryOf(area: Pick<AreaDef, 'category'>): 'music' | 'battle' | 'vehicle' {
  return area.category === 'battle' || area.category === 'vehicle' ? area.category : 'music';
}

// ------------------------------------------------------------------- warming (§9.8)

/**
 * The Nexus's dimension (the mod's `AudioConfig.NEXUS`), for the one place this package needs to know
 * where the hub is: ranking what to warm. It never decides what plays — the catalog's triggers do.
 */
export const AUDIO_HUB_DIMENSION = 'cobblemondimensions:nexus';

/**
 * Whether a vehicle area could play on `ride` at some point of it: a `ride` trigger naming this ride (or any
 * ride), whatever it asks of the motion and the water — those change every few seconds of a tour.
 */
function playsOnRide(area: AreaDef, ride: AudioRide): boolean {
  const trigger = area.trigger;
  return layerOf(area) === 'vehicle' && trigger.kind === 'ride' && (trigger.rides.length === 0 || trigger.rides.includes(ride.id));
}

/**
 * The tracks worth fetching ahead of need, most likely first — what the service worker warms while the
 * tab is idle, so that a cue or a walk between districts starts instantly.
 *
 * THE BUDGET IS THE HUB, AND IT STOPS THERE. The beta library is about 112 MB over 116 files, and a first
 * visit must not turn into a download of it. So there are two tiers for everybody: the cues (a second or so
 * each, tens of kilobytes, and they fire without warning — the vehicle cues among them), then the hub — the
 * Nexus's own area and every district's — because every player arrives there and walks between them.
 *
 * AND ONE MORE ON A RIDE, between the two: while the context has a `ride`, the sounds that ride can make —
 * the first track of each vehicle area whose `ride` trigger names it or any ride — because a ride stops, sets
 * off and splashes within seconds of the context that said it was sat on, and a vehicle layer that waits for
 * a fetch each time is a car that goes quiet as it pulls away.
 *
 * Battles, the other dimensions, the safari's zones and the base are fetched the first time they play and
 * are cache-first from then on. The tracks already playing, voiced lines in any language but the one in
 * force, and ids the manifest lacks are left out; `limit` is a backstop, not the budget.
 */
export function warmOrder(
  catalog: AudioCatalog | null,
  manifest: AudioManifest,
  context: PlayerAudioContext | null,
  options: { readonly playing?: readonly string[]; readonly limit?: number } = {},
): string[] {
  if (catalog === null) return [];
  const limit = options.limit ?? 24;
  const skip = new Set(options.playing ?? []);
  const lang = voiceLanguage(catalog, context);
  const out: string[] = [];
  const add = (id: string | undefined): void => {
    if (id === undefined || out.length >= limit || skip.has(id) || out.includes(id) || !Object.hasOwn(manifest, id)) return;
    const voice = voiceTrackLang(id);
    if (voice !== null && voice !== lang) return;
    out.push(id);
  };

  for (const cue of catalog.cues) add(cue.track);
  const ride = context?.ride;
  if (ride !== undefined) for (const area of catalog.areas) if (playsOnRide(area, ride)) add(area.tracks[0]);
  // Standing in a district names the hub's dimension outright; anywhere else, it is the Nexus.
  const hub = context?.district !== undefined ? context.dimension : AUDIO_HUB_DIMENSION;
  for (const area of catalog.areas) if (area.trigger.kind === 'dimension' && area.trigger.id === hub) add(area.tracks[0]);
  for (const area of catalog.areas) if (area.trigger.kind === 'district') add(area.tracks[0]);
  return out;
}
